import { useEffect, type FC, useState } from "react";
import { useIntl } from "react-intl";
import { patchRefreshToken } from "../../auth";
import {
  isStringOrNull,
  getLocalStorage,
  removeLocalStorage,
  PARTNER_ACCOUNT_ID_STORAGE_KEY,
} from "../../helpers/localStorage";
import { useProfileContext } from "../../portals/messagemedia/providers";
import { useVendorConfigPlugin } from "../../portals/messagemedia/vendorConfig";
import { useZendeskWidget } from "../../portals/messagemedia/zendeskWidget";
import { HorizontalDivider } from "../elements/Dividers";
import { ExtendedProfileAvatar } from "./ProfileAvatar";
import { UsageInfo } from "./UsageInfo";
import { ProfileListButton } from "./components/ProfileListButton";
import { ProfileListItem } from "./components/ProfileListItem";
import { BooksIcon } from "./icons/BooksIcon";
import { SpeechBalloonIcon } from "./icons/SpeechBalloonIcon";

type ProfileContentProps = {
  onClose: () => void;
  logout: () => void;
};
export const ProfileContent: FC<ProfileContentProps> = ({
  onClose,
  logout,
}) => {
  const { name, usageInfo, isSupport, links } = useProfileContext();
  const [partnerId, setPartnerId] = useState<string | undefined>(undefined);
  const vendorPlugin = useVendorConfigPlugin();
  const intl = useIntl();
  const { openWidget } = useZendeskWidget();

  const getSupportLink = () => {
    const vendorSupportLink = vendorPlugin?.getByKey("support-link");
    if (
      vendorPlugin?.isBrandStreetData() ||
      vendorPlugin?.isBrandMobiPost() ||
      vendorPlugin?.isBrandWholeSale() ||
      !vendorSupportLink ||
      typeof vendorSupportLink !== "string"
    ) {
      return links.helpCenter;
    }
    return vendorSupportLink;
  };

  const hasChatLink =
    vendorPlugin?.isBrandMM() ||
    vendorPlugin?.isBrandSMSB() ||
    vendorPlugin?.isBrandTPG() ||
    vendorPlugin?.isBrandSE() ||
    vendorPlugin?.isBrandWholeSale() ||
    vendorPlugin?.isBrandDirectSMS() ||
    vendorPlugin?.isBrandMessageNet() ||
    vendorPlugin?.isBrandMobiPost() ||
    vendorPlugin?.isBrandSMSC() ||
    vendorPlugin?.isBrandStreetData() ||
    vendorPlugin?.isBrandWholeSale() ||
    false;

  // TODO: cleanup after menu-mfe is live
  useEffect(() => {
    const partnerId = getLocalStorage(
      PARTNER_ACCOUNT_ID_STORAGE_KEY,
      isStringOrNull,
      undefined
    );
    if (partnerId) {
      setPartnerId(partnerId);
    }
  }, []);
  const exitPartner = async () => {
    if (partnerId) {
      await patchRefreshToken(
        {
          accountId: partnerId,
          onPatchSuccess: () =>
            removeLocalStorage(PARTNER_ACCOUNT_ID_STORAGE_KEY),
          redirectUrl: "/customer-accounts",
        },
        {
          reason: "switch back",
          roles: ["admin", "user"],
        }
      );
    }
  };

  return (
    <>
      <ExtendedProfileAvatar name={name} onClick={onClose} />
      <UsageInfo usageInfo={usageInfo} />
      {isSupport && (
        <ProfileListItem
          hyperlink
          to={links.supportIndex}
          text={intl.formatMessage({ id: "menuItemProfileSupportDashboard" })}
        />
      )}
      {partnerId && (
        <ProfileListButton
          withSpacing
          onClick={exitPartner}
          text={intl.formatMessage({ id: "menuItemProfileBackToAccounts" })}
        />
      )}
      <HorizontalDivider margin="8px 0" />
      <ProfileListItem
        icon={<BooksIcon />}
        external
        to={getSupportLink()}
        text={intl.formatMessage({ id: "menuItemProfileHelpCenterItem" })}
      />
      {hasChatLink && (
        <ProfileListButton
          withSpacing
          icon={<SpeechBalloonIcon />}
          onClick={() => {
            openWidget();
            onClose();
          }}
          text={intl.formatMessage({
            id: "menuItemProfileChatWithSupportItem",
          })}
        />
      )}
      <HorizontalDivider margin="8px 0" />
      <ProfileListButton
        withSpacing
        onClick={() => {
          logout();
          onClose();
        }}
        text={intl.formatMessage({ id: "menuItemProfileLogoutItem" })}
      />
    </>
  );
};
